<template>
  <PropertyOnboardingStep
    title="What is the address of your listing?"
    :is-next-button-disabled="formRef?.form.validation.$invalid || formRef?.isBusy || portfolioFieldRef?.isLoading"
    :submit="submit"
  >
    <LocationForm ref="formRef">
      <!-- Only while the unit is being created: where an existing unit sits is changed on its Team tab -->
      <template v-if="isNew" #after-address>
        <NewUnitPortfolioField ref="portfolioFieldRef" v-model="portfolioId" :initial="scopedPortfolio" />
      </template>
    </LocationForm>
  </PropertyOnboardingStep>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { AxiosError } from 'axios';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { useAlertsStore } from '@tallo/design-system';
import { companyPortfoliosQueryKey } from '@tallo/investor/company';
import { LocationForm, NewUnitPortfolioField, usePropertyStore } from '@tallo/investor/property';

import PropertyOnboardingStep from '../property-onboarding-step.vue';

const route = useRoute();
const queryCache = useQueryCache();
const propertyStore = usePropertyStore();
const formRef = ref<InstanceType<typeof LocationForm>>();
const portfolioFieldRef = ref<InstanceType<typeof NewUnitPortfolioField>>();
const portfolioId = ref('');

// The same test saveLocation uses to create rather than update
const isNew = computed(() => !propertyStore.property);

// "Add new listing" from a page showing one portfolio, or Unassigned, carries it here
const scopedPortfolio = computed(() => (typeof route.query.portfolio === 'string' ? route.query.portfolio : undefined));

async function submit() {
  try {
    return await formRef.value?.save(isNew.value ? portfolioId.value || undefined : undefined);
  } catch (error) {
    // A rejected field shows on the form; anything else from the server (a portfolio refused
    // meanwhile) is said here, and the step stays where it is
    if (error instanceof AxiosError && !error.response?.data?.validationErrors) {
      useAlertsStore().catchError(error);
      // A portfolio refused here may be gone or no longer the viewer's; the choices re-read
      queryCache.invalidateQueries({ key: [companyPortfoliosQueryKey] }).catch(() => undefined);
    }

    throw error;
  }
}
</script>
